import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IEngine, IEngineMapping } from '~/types/arbitration'

import { DevReviewEngine } from '../Engines'

import { updateDevelopmentEngine } from '~/api/arbitration'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import { arbitrationKeys, useEngineQuery } from '~/queries/arbitration'
import { useGetMeAsUserQuery } from '~/queries/users'
import { ENGINE_TYPE } from '~/types/arbitration'
import { displayToast } from '~/utils/toast'

interface Props {
  selectedApp: IAppSummary
}

const DevelopmentEngine = ({ selectedApp }: Props): ReactElement => {
  const queryClient = useQueryClient()

  const { data: user } = useGetMeAsUserQuery()

  const { data: engine, isLoading: isEngineLoading } = useEngineQuery({
    appKey: selectedApp.app_key,
    deviceType: selectedApp.device_type,
    engineType: ENGINE_TYPE.DEVELOPMENT,
  })

  const { mutateAsync: updateDevelopmentEngineMutation } = useMutation({ mutationFn: updateDevelopmentEngine })

  const handleSubmit = async (mapping: IEngineMapping) => {
    try {
      await updateDevelopmentEngineMutation({
        appKey: selectedApp.app_key,
        deviceType: selectedApp.device_type,
        mapping,
        updatedBy: user?.user_id,
      })
      queryClient.invalidateQueries({
        queryKey: arbitrationKeys.engine(ENGINE_TYPE.DEVELOPMENT, selectedApp.app_key, selectedApp.device_type),
      })

      displayToast('Engine successfully updated', 'success')
    } catch {
      displayToast('Engine could not be updated', 'error')
    }
  }

  if (isEngineLoading) {
    return <MwMLoader category="item" />
  }

  return (
    <Layout>
      <DevReviewEngine engine={engine as IEngine} onSubmit={handleSubmit} selectedApp={selectedApp} />
    </Layout>
  )
}

export default DevelopmentEngine
